import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ArrowRightLeft } from 'lucide-react';
import { useCurrency } from '@/contexts';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import { useTranslation } from '@/hooks/useTranslation';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  account?: Database['public']['Tables']['accounts']['Row'] | null;
};

interface CashFlowChartProps {
  transactions: Transaction[];
  isLoading?: boolean;
}

interface MonthData {
  month: string;
  monthFull: string;
  income: number;
  expense: number;
  net: number;
}

export function CashFlowChart({ transactions, isLoading = false }: CashFlowChartProps) {
  const { formatAmount, formatCompact, isSecretMode, currency } = useCurrency();
  const { getRate } = useExchangeRates();
  const { t, language } = useTranslation();

  // Calculate monthly data for the last 6 months
  const monthlyData = useMemo((): MonthData[] => {
    const months: MonthData[] = [];

    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const monthTransactions = transactions.filter((t) => {
        if (t.is_recurring) return false;
        const tDate = new Date(t.date);
        return tDate >= startOfMonth && tDate <= endOfMonth;
      });

      const income = monthTransactions
        .filter((t) => t.type === 'income')
        .reduce((sum, t) => {
          const acctCurrency = (t as any).account?.currency || 'EUR';
          const rate = getRate(acctCurrency, currency) ?? 1;
          return sum + t.amount * rate;
        }, 0);

      const expense = monthTransactions
        .filter((t) => t.type === 'expense')
        .reduce((sum, t) => {
          const acctCurrency = (t as any).account?.currency || 'EUR';
          const rate = getRate(acctCurrency, currency) ?? 1;
          return sum + t.amount * rate;
        }, 0);

      months.push({
        month: date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short' }),
        monthFull: date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
          month: 'long',
          year: 'numeric',
        }),
        income,
        expense,
        net: income - expense,
      });
    }

    return months;
  }, [transactions, language, currency, getRate]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalIncome = monthlyData.reduce((sum, m) => sum + m.income, 0);
    const totalExpense = monthlyData.reduce((sum, m) => sum + m.expense, 0);
    const avgIncome = totalIncome / 6;
    const avgExpense = totalExpense / 6;
    const avgNet = avgIncome - avgExpense;

    return { totalIncome, totalExpense, avgIncome, avgExpense, avgNet };
  }, [monthlyData]);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6 animate-pulse">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48" />
          </div>
          <div className="flex gap-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
          </div>
        </div>
        <div className="h-52 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ArrowRightLeft className="w-4 h-4 text-gray-400" />
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em]">
              {t('dashboard.cashFlow.title')}
            </p>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('dashboard.cashFlow.subtitle')}
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#16c47f]" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('dashboard.stats.income')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#78909C]" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('dashboard.stats.expenses')}
            </span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={monthlyData} barCategoryGap="20%">
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              tickFormatter={(v: number) => (isSecretMode ? '•••' : formatCompact(v))}
              width={60}
            />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.05)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const data = payload[0]?.payload as MonthData;
                return (
                  <div
                    style={{
                      background: 'rgba(17, 24, 39, 0.95)',
                      backdropFilter: 'blur(12px)',
                      borderRadius: '14px',
                      padding: '14px 18px',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                      minWidth: '180px',
                    }}
                  >
                    <div
                      style={{
                        color: '#9ca3af',
                        fontSize: '12px',
                        marginBottom: '12px',
                        fontWeight: 500,
                      }}
                    >
                      {data.monthFull}
                    </div>

                    {/* Income */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#16c47f',
                          }}
                        />
                        <span style={{ color: '#e5e7eb', fontSize: '12px' }}>
                          {t('dashboard.stats.income')}
                        </span>
                      </div>
                      <span style={{ color: '#16c47f', fontSize: '14px', fontWeight: 700 }}>
                        {isSecretMode ? '•••' : formatAmount(data.income)}
                      </span>
                    </div>

                    {/* Expenses */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#78909C',
                          }}
                        />
                        <span style={{ color: '#e5e7eb', fontSize: '12px' }}>
                          {t('dashboard.stats.expenses')}
                        </span>
                      </div>
                      <span style={{ color: '#78909C', fontSize: '14px', fontWeight: 700 }}>
                        {isSecretMode ? '•••' : formatAmount(data.expense)}
                      </span>
                    </div>

                    {/* Divider */}
                    <div
                      style={{
                        height: '1px',
                        background: 'rgba(255,255,255,0.1)',
                        marginBottom: '10px',
                      }}
                    />

                    {/* Net */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                        {t('dashboard.cashFlow.balance' as any)}
                      </span>
                      <span
                        style={{
                          color: data.net >= 0 ? '#16c47f' : '#ef4444',
                          fontSize: '15px',
                          fontWeight: 700,
                        }}
                      >
                        {data.net >= 0 ? '+' : ''}
                        {isSecretMode ? '•••' : formatAmount(data.net)}
                      </span>
                    </div>
                  </div>
                );
              }}
            />
            <ReferenceLine y={0} stroke="#e5e7eb" strokeDasharray="3 3" />
            <Bar dataKey="income" fill="#16c47f" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar dataKey="expense" fill="#78909C" radius={[4, 4, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary footer */}
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50 grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
            {t('dashboard.cashFlow.avgIncome' as any)}
          </p>
          <p className="text-sm font-bold text-[#16c47f] tabular-nums">
            {isSecretMode ? '•••' : formatAmount(summary.avgIncome)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
            {t('dashboard.cashFlow.avgExpenses' as any)}
          </p>
          <p className="text-sm font-bold text-[#78909C] tabular-nums">
            {isSecretMode ? '•••' : formatAmount(summary.avgExpense)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
            {t('dashboard.cashFlow.avgBalance' as any)}
          </p>
          <p
            className={`text-sm font-bold tabular-nums ${summary.avgNet >= 0 ? 'text-[#16c47f]' : 'text-red-500'}`}
          >
            {summary.avgNet >= 0 ? '+' : ''}
            {isSecretMode ? '•••' : formatAmount(summary.avgNet)}
          </p>
        </div>
      </div>
    </div>
  );
}
